import type { ImgHTMLAttributes } from "react";

/**
 * Output shape of vite-imagetools `?as=picture` query.
 * Sources are format → srcset string (with width descriptors).
 */
export type PictureSource = {
  sources: Record<string, string>;
  img: { src: string; w: number; h: number };
};

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> & {
  picture: PictureSource;
  alt: string;
  sizes?: string;
  /** Render order. AVIF first, WebP second, original fallback last. */
  formatPriority?: ReadonlyArray<string>;
};

const MIME: Record<string, string> = {
  avif: "image/avif",
  webp: "image/webp",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
};

const DEFAULT_ORDER = ["avif", "webp"] as const;

export function ResponsivePicture({
  picture,
  alt,
  sizes,
  formatPriority = DEFAULT_ORDER,
  loading = "lazy",
  decoding = "async",
  className,
  ...imgProps
}: Props) {
  const { sources, img } = picture;
  return (
    <picture>
      {formatPriority.map((fmt) =>
        sources[fmt] ? (
          <source
            key={fmt}
            type={MIME[fmt] ?? `image/${fmt}`}
            srcSet={sources[fmt]}
            sizes={sizes}
          />
        ) : null,
      )}
      <img
        src={img.src}
        width={img.w}
        height={img.h}
        alt={alt}
        loading={loading}
        decoding={decoding}
        sizes={sizes}
        className={className}
        {...imgProps}
      />
    </picture>
  );
}
